/**
 * Folder admin dialogs for the Documents page: new main folder, rename,
 * delete, and new sub-folder.
 *
 * Moved out of FolderTile.tsx and Documents.tsx (CR-7754) so both stay under
 * the 500-line cap; the dialog bodies are unchanged.
 */

import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { CategoryWithDocuments, DocumentCategory, documentsAPI } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { buildFolderOptions } from './utils';

interface NewFolderDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/** Create a main (top-level) folder. Customer admins only. */

export function NewFolderDialog({ open, onClose, onCreated }: NewFolderDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await documentsAPI.createCategory({ name: name.trim(), description: description.trim() });
      toast.success(`Folder "${name.trim()}" created`);
      setName(''); setDescription('');
      onCreated();
      onClose();
    } catch (err) {
      // CR-5326: surface the backend's friendly duplicate-folder message
      // ("A folder with this name already exists."). Helix's error
      // middleware wraps validation errors as
      // {details: {fields: {name: [msg]}}} — getApiErrorMessage walks
      // that shape so we don't have to duplicate the unwrap logic.
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = status === 403
        ? 'Only customer admins can create folders.'
        : getApiErrorMessage(err, 'Failed to create folder');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Name <span className="text-red-500">*</span></Label>
            <Input id="folder-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Compliance Certificates" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="folder-desc">Description</Label>
            <Textarea id="folder-desc" value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Optional — shown under the folder title" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : 'Create Folder'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface RenameFolderDialogProps {
  category: CategoryWithDocuments;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Rename a folder. Customer-only — only mounted when the caller has
 * permission to mutate folders. Surfaces backend validation errors via
 * sonner (e.g. duplicate folder name).
 */
export function RenameFolderDialog({ category, onClose, onSaved }: RenameFolderDialogProps) {
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !category.id) return;
    setSaving(true);
    try {
      await documentsAPI.updateCategory(category.id, {
        name: name.trim(),
        description: description.trim(),
      });
      toast.success(`Folder renamed to "${name.trim()}"`);
      onSaved();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = status === 403
        ? 'Only customer admins can rename folders.'
        : getApiErrorMessage(err, 'Failed to rename folder');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rename folder</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rename-folder-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="rename-folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rename-folder-desc">Description</Label>
            <Textarea
              id="rename-folder-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteFolderDialogProps {
  category: CategoryWithDocuments;
  onClose: () => void;
  onDeleted: () => void;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Ids of every folder nested under `rootId`, at any depth.
 *
 * @param folders - the flat folder list
 * @param rootId - the folder whose descendants are wanted
 * @returns the descendant ids (not including `rootId`)
 */
function descendantIds(folders: DocumentCategory[], rootId: number): Set<number> {
  const childrenOf = new Map<number, number[]>();
  folders.forEach((f) => {
    if (f.parent != null) childrenOf.set(f.parent, [...(childrenOf.get(f.parent) ?? []), f.id]);
  });
  const inside = new Set<number>();
  const stack = [...(childrenOf.get(rootId) ?? [])];
  while (stack.length) {
    const id = stack.pop() as number;
    if (inside.has(id)) continue;
    inside.add(id);
    stack.push(...(childrenOf.get(id) ?? []));
  }
  return inside;
}

/**
 * CR-5379 / CR-7754: Confirm + execute folder deletion with explicit
 * handling for everything inside — the folder's own documents AND its
 * sub-folders at any depth. The customer chooses one of two modes:
 *
 *  - "Delete folder AND everything in it" (cascade) — the sub-folders and
 *    every file in the tree are deleted
 *  - "Delete folder only — move … to …" — every file in the tree moves to
 *    another folder (never one inside the tree) or to Uncategorised; the
 *    sub-folders are removed
 *
 * The file count comes from the server when the window opens: the folder
 * list can be narrowed by an active search, the delete never is. Each
 * per-file action is audit-logged on the backend so Audit History still
 * reflects every move/delete.
 */
export function DeleteFolderDialog({ category, onClose, onDeleted }: DeleteFolderDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [mode, setMode] = useState<'reassign' | 'cascade'>('reassign');
  const [reassignTo, setReassignTo] = useState<string>(''); // '' = Uncategorised
  // Reassign-target picker only needs id/name, so a flat DocumentCategory
  // list is enough (and lets us reuse getAllCategories).
  const [folders, setFolders] = useState<DocumentCategory[]>([]);
  const [count, setCount] = useState(category.total_document_count ?? category.document_count);
  const [counting, setCounting] = useState(true);
  const hasDocs = count > 0;
  const subfolderCount = category.subfolder_count ?? 0;
  const hasSubfolders = subfolderCount > 0;
  const files = plural(count, 'file', 'files');
  const subs = plural(subfolderCount, 'sub-folder', 'sub-folders');

  useEffect(() => {
    if (!category.id) return;
    const folderId = category.id;
    let cancelled = false;
    Promise.all([
      // getAllCategories walks every page — page_size:500 was silently capped
      // at the server max (100), so tenants with more folders lost reassign
      // targets beyond the first 100.
      documentsAPI.getAllCategories()
        .then((arr) => {
          if (cancelled) return;
          // Never offer the folder being deleted, or anything inside it, as
          // the destination — those are removed too.
          const inside = descendantIds(arr, folderId);
          setFolders(arr.filter((f) => f.id !== folderId && !inside.has(f.id)));
        })
        .catch(() => {/* picker stays minimal — Uncategorised always available */}),
      documentsAPI.getCategoryDetails(folderId)
        .then((res) => { if (!cancelled) setCount(res.data.total_file_count); })
        .catch(() => {/* keep the folder list's count */}),
    ]).finally(() => { if (!cancelled) setCounting(false); });
    return () => { cancelled = true; };
  }, [category.id]);

  const handleDelete = async () => {
    if (!category.id) return;
    setDeleting(true);
    try {
      const opts: { cascade?: boolean; reassignTo?: number | null } = hasDocs && mode === 'cascade'
        ? { cascade: true }
        : { reassignTo: reassignTo ? Number(reassignTo) : null };
      await documentsAPI.deleteCategory(category.id, opts);
      const docs = plural(count, 'document', 'documents');
      const target = reassignTo
        ? folders.find(f => f.id === Number(reassignTo))?.name || 'selected folder'
        : 'Uncategorised';
      const suffix = !hasDocs
        ? (hasSubfolders ? ` with ${subs}` : '')
        : mode === 'cascade'
          ? (hasSubfolders ? ` with ${subs} and ${docs}` : ` and ${docs}`)
          : ` — ${docs} moved to ${target}${hasSubfolders ? `, ${subs} removed` : ''}`;
      toast.success(`Folder "${category.name}" deleted${suffix}`);
      onDeleted();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = status === 403
        ? 'Only customer admins can delete folders.'
        : getApiErrorMessage(err, 'Failed to delete folder');
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete folder?</DialogTitle>
          <DialogDescription>
            This will remove{' '}
            <span className="font-medium text-foreground">&quot;{category.name}&quot;</span>
            {hasSubfolders
              ? (hasDocs
                ? `. It holds ${subs} and ${files} in total — choose what happens to them.`
                : `. It holds ${subs} with no files in them — they will be deleted too.`)
              : hasDocs
                ? `. It contains ${count} document${count === 1 ? '' : 's'} — choose what happens to ${count === 1 ? 'it' : 'them'}.`
                : '. The folder is empty.'}
          </DialogDescription>
        </DialogHeader>

        {hasDocs && (
          <div className="space-y-3 py-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="folder-delete-mode"
                value="reassign"
                checked={mode === 'reassign'}
                onChange={() => setMode('reassign')}
                className="mt-1"
              />
              <div className="flex-1 text-sm">
                <span className="font-medium">
                  {hasSubfolders
                    ? `Delete folder only — move ${count === 1 ? 'the file' : `all ${files}`} to…`
                    : 'Delete folder only — move documents to…'}
                </span>
                <select
                  value={reassignTo}
                  onChange={(e) => setReassignTo(e.target.value)}
                  disabled={mode !== 'reassign'}
                  aria-label="Move documents to"
                  className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm disabled:opacity-50"
                >
                  <option value="">Uncategorised</option>
                  {buildFolderOptions(folders).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="folder-delete-mode"
                value="cascade"
                checked={mode === 'cascade'}
                onChange={() => setMode('cascade')}
                className="mt-1"
              />
              <div className="flex-1 text-sm">
                <span className="font-medium text-destructive">
                  {hasSubfolders ? 'Delete folder AND everything in it' : 'Delete folder AND all its documents'}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {hasSubfolders
                    ? `The ${subs} and ${files} will be permanently deleted. History is kept.`
                    : `The ${count} document${count === 1 ? '' : 's'} inside will be permanently deleted. Audit history is preserved.`}
                </p>
              </div>
            </label>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting || counting}
          >
            {deleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting…
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />Delete folder
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CreateSubfolderDialogProps {
  parent: CategoryWithDocuments;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Create a folder nested under `parent`. Only mounted when the current
 * user may nest folders (customer users, staff, or super admins — see
 * CanCreateSubfolder on the backend). Surfaces the backend's duplicate-name
 * and permission errors via sonner.
 */
export function CreateSubfolderDialog({ parent, onClose, onCreated }: CreateSubfolderDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !parent.id) return;
    setSaving(true);
    try {
      await documentsAPI.createCategory({
        name: name.trim(),
        description: description.trim(),
        parent: parent.id,
      });
      toast.success(`Subfolder "${name.trim()}" created in "${parent.name}"`);
      onCreated();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = status === 403
        ? 'Only customer users, staff, or super admins can create subfolders.'
        : getApiErrorMessage(err, 'Failed to create subfolder');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New subfolder</DialogTitle>
          <DialogDescription>
            Nested inside{' '}
            <span className="font-medium text-foreground">&quot;{parent.name}&quot;</span>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subfolder-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="subfolder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 2026 Audits"
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="subfolder-desc">Description</Label>
            <Textarea
              id="subfolder-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…
                </>
              ) : (
                'Create subfolder'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
